<template>
  <v-container fluid>

    <!-- Language selector -->
    <div class="pt-3 pb-1">
      <div class="field-label text-left pl-4">
        {{ $t('dash.shadowTags.selectLanguage') }}
      </div>
      <v-select
        v-model="selectedLanguage"
        :items="languageOptions"
        item-text="label"
        item-value="code"
        outlined
        dense
        hide-details
        @change="loadTags"
      />
    </div>

    <!-- Search bar + scope chips (visible once a language is loaded) -->
    <div v-if="tags.length > 0" class="pb-3">
      <div class="field-label text-left pl-4">
        {{ $t('dash.shadowTags.search') }}
      </div>
      <div class="d-flex align-center">
        <v-text-field
          v-model="searchQuery"
          append-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          class="flex-grow-1"
        />
        <!-- Search scope chips -->
        <div class="d-flex align-center ml-2 flex-shrink-0">
          <v-chip
            v-for="opt in scopeOptions"
            :key="opt.value"
            x-small
            :color="searchScope === opt.value ? '#00A9B3' : ''"
            :outlined="searchScope !== opt.value"
            :dark="searchScope === opt.value"
            class="scope-chip mr-1"
            @click="searchScope = opt.value"
          >
            <!-- Icon variant (translate icon) or plain text label -->
            <img
              v-if="opt.icon"
              :src="opt.icon"
              :alt="$t('dash.shadowTags.scopeTranslated')"
              :class="['scope-icon', { 'scope-icon--active': searchScope === opt.value }]"
            />
            <span v-else>{{ opt.label }}</span>
          </v-chip>
        </div>
      </div>
      <!-- Manual-only filter: shows entries that were manually overridden -->
      <div class="mt-2 d-flex align-center">
        <v-chip
          x-small
          :color="showManualOnly ? 'orange darken-1' : ''"
          :outlined="!showManualOnly"
          :dark="showManualOnly"
          class="manual-chip"
          @click="toggleManualFilter"
        >
          {{ $t('dash.shadowTags.filterManual') }}
        </v-chip>
        <v-chip
          x-small
          :color="showAttentionOnly ? 'red darken-1' : ''"
          :outlined="!showAttentionOnly"
          :dark="showAttentionOnly"
          class="attention-chip ml-2"
          @click="toggleAttentionFilter"
        >
          {{ $t('dash.shadowTags.filterAttention') }}
        </v-chip>
      </div>
    </div>

    <!-- Loading spinner -->
    <div v-if="loading" class="d-flex justify-center pa-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <!-- Global error -->
    <v-alert
      v-if="globalError"
      type="error"
      class="ma-4"
      dense
      dismissible
      @input="globalError = null"
    >
      {{ globalError }}
    </v-alert>

    <!-- Empty state -->
    <div
      v-if="!loading && selectedLanguage && tags.length === 0 && !globalError"
      class="pa-4 text-center grey--text"
    >
      {{ $t('dash.shadowTags.noEntries') }}
    </div>

    <!-- Tag list -->
    <div v-for="tag in filteredTags" :key="tag.id">

      <!-- Collapsed row -->
      <DetailListItem
        @click.native="toggleTag(tag)"
        icon-right
        icon="arrow-down-pink.svg"
        height="auto"
      >
        <div class="tag-row">
          <!-- Show as hashtag-style keywords -->
          <span class="original-text">#{{ tag.originalName }}</span>
          <span class="divider-text">&#8594;</span>
          <span :class="['translated-text', tag.editable ? 'text--warning' : '']">
            #{{ tag.name }}
          </span>
          <v-chip
            x-small
            :color="tag.attention ? 'red darken-1' : (tag.editable ? 'orange darken-1' : 'teal')"
            dark
            class="ml-2"
          >
            {{ tag.attention
              ? $t('dash.shadowTags.statusAttention')
              : (tag.editable
                ? $t('dash.shadowTags.statusManual')
                : $t('dash.shadowTags.statusAuto')
              )
            }}
          </v-chip>
        </div>
      </DetailListItem>

      <!-- Expanded section -->
      <div
        :id="'shadow-tag-' + tag.id + '-content'"
        class="content-closed tag-content pa-3"
      >

        <!-- Original Dutch source -->
        <div class="original-block mb-3 pa-3">
          <div class="caption grey--text mb-1 text-left">
            {{ $t('dash.shadowTags.original') }}
          </div>
          <div class="subtitle-2 font-weight-bold text-left">
            #{{ tag.originalName }}
          </div>

        </div>

        <!-- Translated name -->
        <div class="caption grey--text text-left mb-1">{{ $t('dash.shadowTags.translatedName') }}</div>
        <v-text-field
          v-model="tag.name"
          outlined
          dense
          hide-details
          class="mb-2"
        />

        <!-- Feedback messages -->
        <v-alert v-if="tag.successMessage" type="success" dense class="mb-2">
          {{ tag.successMessage }}
        </v-alert>

        <v-alert v-if="tag.errorMessage" type="error" dense class="mb-2">
          {{ tag.errorMessage }}
        </v-alert>

        <!-- Action buttons -->
        <div class="d-flex align-center">
          <!-- PUT /api/shadow-tags/{id} -->
          <v-btn
            color="primary"
            small
            :loading="tag.saving"
            :disabled="!tag.name"
            @click="saveTag(tag)"
          >
            {{ $t('dash.shadowTags.save') }}
          </v-btn>

          <v-btn
            v-if="tag.attention"
            outlined
            small
            color="#C62828"
            :loading="tag.saving"
            class="ml-2"
            @click="removeAttention(tag)"
          >
            {{ $t('dash.shadowTags.removeAttention') }}
          </v-btn>

          <!-- POST /api/shadow-tags/{id}/reset — only visible when manually overridden -->
          <v-btn
            v-if="tag.editable"
            text
            small
            color="warning"
            :loading="tag.resetting"
            class="ml-2"
            @click="resetTag(tag)"
          >
            {{ $t('dash.shadowTags.reset') }}
          </v-btn>
        </div>

      </div>
    </div>

  </v-container>
</template>

<script>
import { neoparentShadowTags } from "@/api";
import { redirect } from "@/helpers";

export default {
  name: "neoparent-shadow-tags",

  data() {
    return {
      selectedLanguage: null,
      tags: [],
      loading: false,
      globalError: null,
      searchQuery: "",
      searchDebounced: "",
      // Search scope: 'original' | 'translated' | 'both'
      searchScope: "both",
      // When true, only manually overridden entries are shown
      showManualOnly: false,
      // When true, only exact-match entries are shown
      showAttentionOnly: false,
      availableLanguages: []
    };
  },

  watch: {
    searchQuery(val) {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.searchDebounced = val || "";
      }, 500);
    }
  },

  computed: {
    filteredTags() {
      let list = this.showManualOnly ? this.tags.filter(t => t.editable) : this.tags;
      if (this.showAttentionOnly) {
        list = list.filter(t => t.attention);
      }
      const q = this.searchDebounced.trim().toLowerCase();
      if (!q) return list;
      return list.filter(t => {
        const inOriginal = (t.originalName || "").toLowerCase().includes(q);
        const inTranslated = (t.name || "").toLowerCase().includes(q);
        if (this.searchScope === "original") return inOriginal;
        if (this.searchScope === "translated") return inTranslated;
        return inOriginal || inTranslated;
      });
    },

    scopeOptions() {
      // Use the hospital's configured default language instead of hardcoded "NL"
      const sourceLang = (this.$store.getters.user.hospital?.defaultLanguage || "nl")
        .split("-")[0]
        .toUpperCase();
      return [
        { value: "original",   label: sourceLang },
        // Translate icon replaces text for the "translated" scope
        { value: "translated", icon: require("@/assets/icons/translate.svg") },
        { value: "both",       label: this.$t("dash.shadowTags.scopeBoth") }
      ];
    },

    languageOptions() {
      return this.availableLanguages.map(l => ({
        code: l.code,
        label: l.displayName || this.$t("general.languages." + l.code)
      }));
    }
  },

  async mounted() {
    if (this.$store.getters.user.role !== "a") {
      redirect("/dashboard");
      return;
    }

    await this.loadLanguageOptions();
  },

  methods: {

    async loadLanguageOptions() {
      try {
        const res = await neoparentShadowTags.getLanguages();
        this.availableLanguages = res.data?.languages || [];
      } catch (err) {
        this.availableLanguages = [];
        this.globalError = err?.response?.data?.message || "Error loading languages.";
      }
    },

    toggleTag(tag) {
      const el = document.getElementById("shadow-tag-" + tag.id + "-content");
      if (!el) return;
      el.classList.toggle("content-closed");
    },

    hasExactMatch(tag) {
      const original = (tag.originalName || "").trim();
      const translated = (tag.name || "").trim();
      return original.length > 0 && original === translated;
    },

    isCreationState(tag) {
      if (!tag || !tag.createdAt || !tag.updatedAt) return false;
      const created = new Date(tag.createdAt).getTime();
      const updated = new Date(tag.updatedAt).getTime();
      if (!Number.isFinite(created) || !Number.isFinite(updated)) return false;

      // CreatedAt and UpdatedAt are set independently on insert.
      return Math.abs(updated - created) <= 2000;
    },

    shouldAssignAttention(tag) {
      return !tag.editable && this.isCreationState(tag) && this.hasExactMatch(tag);
    },

    toggleAttentionFilter() {
      if (!this.showAttentionOnly) {
        this.showManualOnly = false;
      }
      this.showAttentionOnly = !this.showAttentionOnly;
    },

    toggleManualFilter() {
      if (!this.showManualOnly) {
        this.showAttentionOnly = false;
      }
      this.showManualOnly = !this.showManualOnly;
    },

    async loadTags() {
      if (!this.selectedLanguage) return;

      this.loading = true;
      this.globalError = null;
      this.tags = [];
      this.searchQuery = "";
      this.searchDebounced = "";
      this.showManualOnly = false;
      this.showAttentionOnly = false;

      try {
        const res = await neoparentShadowTags.getByLanguage(this.selectedLanguage);
        this.tags = (res.data.entries || []).map(t => ({
          ...t,
          // Attention is only assigned once for creation-time exact matches.
          attention: this.shouldAssignAttention(t),
          saving: false,
          resetting: false,
          successMessage: null,
          errorMessage: null
        }));
      } catch (err) {
        this.globalError = err?.response?.data?.message || "Error loading tags.";
      } finally {
        this.loading = false;
      }
    },

    async saveTag(tag) {
      if (!tag.name) return;

      tag.saving = true;
      tag.successMessage = null;
      tag.errorMessage = null;

      try {
        const res = await neoparentShadowTags.updateManual(tag.id, {
          name: tag.name.trim()
        });

        tag.name = res.data.entry.name;
        tag.editable = res.data.entry.editable;
        tag.attention = false;
        tag.successMessage = this.$t("dash.shadowTags.saveSuccess");

        setTimeout(() => { tag.successMessage = null; }, 3000);
      } catch (err) {
        tag.errorMessage = err?.response?.data?.message || "Error saving tag.";
      } finally {
        tag.saving = false;
      }
    },

    async removeAttention(tag) {
      if (!tag || !tag.attention) return;

      tag.saving = true;
      tag.successMessage = null;
      tag.errorMessage = null;

      try {
        const res = await neoparentShadowTags.updateManual(tag.id, {
          name: (tag.name || "").trim(),
          label: tag.label
        });

        tag.name = res.data.entry.name;
        tag.editable = res.data.entry.editable;
        tag.attention = false;
        tag.successMessage = this.$t("dash.shadowTags.saveSuccess");

        setTimeout(() => { tag.successMessage = null; }, 3000);
      } catch (err) {
        tag.errorMessage = err?.response?.data?.message || "Error removing attention.";
      } finally {
        tag.saving = false;
      }
    },

    async resetTag(tag) {
      if (!confirm(this.$t("dash.shadowTags.resetConfirm"))) return;

      tag.resetting = true;

      try {
        const res = await neoparentShadowTags.resetToAuto(tag.id);
        tag.name = res.data.entry.name;
        tag.editable = res.data.entry.editable;
        tag.attention = false;
      } catch (err) {
        tag.errorMessage = err?.response?.data?.message || "Error resetting tag.";
      } finally {
        tag.resetting = false;
      }
    }
  }
};
</script>

<style scoped>
.tag-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.original-text {
  font-weight: 500;
}

.divider-text {
  color: #9e9e9e;
  margin: 0 4px;
}

.original-block {
  background-color: #f5f5f5;
  border-radius: 4px;
}

.field-label {
  font-size: 0.75rem;
  color: #888;
  margin-bottom: 4px;
  letter-spacing: 0.02em;
}

/* Inactive scope chips use the app's mint border */
.scope-chip.v-chip--outlined {
  border-color: #afdfd3 !important;
  color: #9e9e9e !important;
}

.scope-chip.v-chip--outlined:hover {
  background-color: #D9F0EA !important;
  color: #00A9B3 !important;
}

/* Translate icon inside scope chip */
.scope-icon {
  width: 16px;
  height: 16px;
  display: block;
}

/* When the chip is active (teal bg), invert icon to white */
.scope-icon--active {
  filter: brightness(0) invert(1);
}

/* Manual filter chip: outlined orange when inactive */
.manual-chip.v-chip--outlined {
  border-color: #EF6C00 !important;
  color: #EF6C00 !important;
}

.manual-chip.v-chip--outlined:hover {
  background-color: #FFF3E0 !important;
}

/* Attention filter chip: outlined red when inactive */
.attention-chip.v-chip--outlined {
  border-color: #C62828 !important;
  color: #C62828 !important;
}

.attention-chip.v-chip--outlined:hover {
  background-color: #FFEBEE !important;
}

</style>
